"use client";

import { useAuth, useUser } from '@clerk/nextjs';
import { uploadFile } from '@/lib/upload';
import { toast } from 'sonner';
import { useRef } from 'react';
import type { PropsWithChildren } from 'react';
import { useProject } from '@/providers/project';
import { openPinturaVideoModal } from '@/components/nodes/pintura/open';
import { useI18n } from '@/providers/i18n';

type Props = PropsWithChildren<{
  src: string;
  disabled?: boolean;
  filenamePrefix?: string;
  className?: string;
  onUpdated: (result: { url: string; type: string; width?: number; height?: number; durationSeconds?: number; aspectRatio?: string; size?: string }) => void;
  onUploadingChange?: (uploading: boolean) => void;
  fps?: number;
  mimeType?: string;
}>;

export const EditWithPinturaVideoOnDoubleClick = ({
  children,
  src,
  disabled,
  filenamePrefix = 'video-edit',
  className,
  onUpdated,
  onUploadingChange,
  fps,
  mimeType,
}: Props) => {
  const { getToken } = useAuth();
  const { user } = useUser();
  const project = useProject();
  const { t } = useI18n();
  const clickTimerRef = useRef<number | undefined>(undefined);

  const handleDoubleClick = async () => {
    if (disabled) return;
    try {
      if (clickTimerRef.current) {
        window.clearTimeout(clickTimerRef.current);
        clickTimerRef.current = undefined;
      }
      const edited = await openPinturaVideoModal({ src, fps, mimeType });
      if (!edited) return;

      onUploadingChange?.(true);
      const token = await getToken({ template: 'supabase' });
      if (!token || !user?.id) {
        toast.error(t('Please sign in to upload'));
        onUploadingChange?.(false);
        return;
      }

      const inferredExt = edited.file.name.split('.').pop();
      const ext = inferredExt || ((mimeType ?? edited.file.type).includes('mp4') ? 'mp4' : 'webm');
      const finalType = edited.file.type || mimeType || 'video/webm';
      const file = new File([edited.file], `${filenamePrefix}-${Date.now()}.${ext}`, { type: finalType });
      const { url, type } = await uploadFile(file, 'files', {
        token,
        userId: user.id,
        projectId: String(project?.id || ''),
        assetType: 'video',
      });
      onUpdated({
        url,
        type,
        width: edited.width,
        height: edited.height,
        durationSeconds: edited.durationSeconds,
        aspectRatio: edited.aspectRatio,
        size: edited.size,
      });
      toast.success(t('Video updated'));
    } catch (e) {
      console.error(e);
      toast.error(t('Failed to edit video'));
    } finally {
      onUploadingChange?.(false);
    }
  };

  return (
    <div className={className} onDoubleClick={handleDoubleClick}>
      {children}
    </div>
  );
};


